// Post type definitions — extracted from app/src/types/post.ts (pre-Phase-12).
// Pure types + runtime enums + pure type-guard helpers. No external runtime deps.

import type { Vector3Tuple } from "three";
import type { PossibleEasings } from "./easings.js";
import type { TextContent } from "./layer-content.js";

/**
 * Font information DTO — inlined from scrollyfrontend server actions
 */
export interface FontInfoDto {
    familyName: string;
    weight: number;
    italic: boolean;
    variable: boolean;
    filename: string;
    fontUrl: string;
}

/**
 * Tenant handling in our new system is still undefined. The template JSON does include them, but we should not use or rely on them.
 */
enum RealTenantId {
    SCRLLY = "scrlly",
    CORPORATE = "corporate",
    FIVETIMES = "five-times",
    MERCEDES = "mercedes",
    TEST = "test",
    ZEIT = "zeit",
    DVAG = "dvag",
    TWENTYR = "20r",
    SEACLOUD = "seacloud",
    DISCOVERSEACLOUD = "discoverseacloud",
    GREENTECH = "greentech",
    PLAYGROUND = "playground",
    MEDIAIMPACT = "mediaimpact",
    HANDELSZEITUNG = "handelszeitung",
    BEOBACHTER = "beobachter",
    SPARKASSE = "sparkasse",
    VERTICAL52 = "vertical52",
    NZZ = "nzz",
    HORNBACH = "hornbach",
    MML = "mml",
    LEMON = "lemon",
    TRACK = "track",
    HITCHON = "hitchon",
    PRESTEL = "prestel",
    WIWO = "wiwo",
    SPECTARIS = "spectaris",
    EOMUC = "eomuc",
    STEPSTONE = "stepstone",
    SCROLLYAI = "scrollyai",
    BOESCH = "boesch",
    VIENNA_AT = "vienna-at",
    AOK = "aok",
    GARCIAMEDIA = "garciamedia",
    BILD = "bild",
    FOCUS = "focus",
    TUICRUISES = "tuicruises",
    DESIGNBESTSELLER = "design-bestseller",
    VORWERK = "vorwerk",
    ABENDBLATT = "abendblatt",
    EDUCATION = "education",
    DROEMER_KNAUR = "droemer-knaur",
    MJNT = "mjnt",
    EDELSTOFFMEDIA = "edelstoffmedia",
    HOWTO = "how-to",
    REGENBOGEN = "regenbogen",
    FOODBOOM = "foodboom",
    MIKA = "mika",
    BVB = "bvb",
    MKVISUAL = "mk-visual",
    VOLKSBANK = "volksbank",
    BESTOF = "best-of",
    LZMEDIEN = "lzmedien",
    PPI = "ppi",
    BIOLAND = "bioland",
}

export enum PostVisibility {
    /** Listed and viewable for all */
    public = "public",
    /** Listed for all, but viewable only for signed-in users */
    private = "private",
    /** Not listed, but viewable for all */
    hidden = "hidden",
}

export interface Color {
    r: number;
    g: number;
    b: number;
    a: number;
}

export interface PresentationOptionsSerialized {
    ambientIntensity: number;
    slideOptions: SlideOptionsSerialized;
}

export interface FontMapping {
    headline?: { family: string; weight: number };
    text?: { family: string; weight: number };
}

export interface SlideOptionsSerialized {
    scrollable: boolean;
    safeAreaYPosition: number;
    safeAreaXPosition: number;
    backgroundColor: Color;
    fontColor: Color;
    fontInfos?: FontInfoDto[];
    fontMapping?: FontMapping;
    companyLogoPosition?: "left" | "center" | "none";
    companyLogoUrl?: string;
    padding: number;
    borderRadius: number;
    animationType: string;
    alignAtSafeArea: boolean;
    margin: number;
    companyBoxPosition: "left" | "center" | "right";
}

export interface SlideSerialized {
    from: number;
    name: string;
    id: string;
    topFleshColor: Color;
    bottomFleshColor: Color;
    text: {
        text: string;
        show?: boolean;
    };
    credits: {
        text: string;
        light: boolean;
        photographerUrl?: string;
    };
    options: SlideOptionsSerialized;
    isChapter: boolean;
    /** Glasmorphism: backdrop-filter blur on the text container */
    blurBackground?: boolean;
    /** Drop shadow on the text container */
    boxShadow?: boolean;
}

export enum LayerType {
    text = "text",
    svg = "svg",
    color = "color",
    image = "image",
    video = "video",
    lottie = "lottie",
    group = "group", // the content is an array of other layers
    object3d = "object3d",
    object3dChild = "object3dChild",
    light = "light",
    map = "map",
}

export interface KeyframeSerialized {
    start: number;
    value: unknown;
    id?: string;
    easing: PossibleEasings;
}

export enum AnimationType {
    position = "position",
    rotation = "rotation",
    scale = "scale",
    opacity = "opacity",
}

export interface AnimationGroupSerialized {
    id: string;
    initialValue: unknown;
    keyframes: Array<KeyframeSerialized>;
    active: boolean;
    type: AnimationType;
}

export interface LayerSerialized {
    id: string;
    name: string;
    type: LayerType;
    from: number;
    to: number;
    visible: boolean;
    pivotPoint?: Vector3Tuple;
    animationsGroups: {
        opacity: AnimationGroupSerialized;
        rotation: AnimationGroupSerialized;
        position: AnimationGroupSerialized;
        scale: AnimationGroupSerialized;
    };
    content: unknown;
}

export interface MediaLayerSerialized extends LayerSerialized {
    type: LayerType.image | LayerType.video | LayerType.lottie;
    isBackground?: boolean;
    content: ImageAssetBackend | VideoAssetBackend;
}

interface VideoLayerSerialized extends MediaLayerSerialized {
    type: LayerType.video;
    content: VideoAssetBackend;
}

interface ImageLayerSerialized extends MediaLayerSerialized {
    type: LayerType.image;
    content: ImageAssetBackend;
}

export function isImageLayer(layer: LayerSerialized): layer is ImageLayerSerialized {
    return layer.type === LayerType.image || layer.type === LayerType.svg;
}

export function isVideoLayer(layer: LayerSerialized): layer is VideoLayerSerialized {
    return layer.type === LayerType.video;
}

export function isLottieLayer(layer: LayerSerialized): boolean {
    return layer.type === LayerType.lottie;
}

export interface TextLayerSerialized extends LayerSerialized {
    type: LayerType.text;
    content: TextContent;
}

export function isTextLayer(layer: LayerSerialized): layer is TextLayerSerialized {
    return layer.type === LayerType.text;
}

export interface PresentationSerialized {
    presentationOptions: PresentationOptionsSerialized;
    id: string;
    slides: Array<SlideSerialized>;
    groups: Array<LayerSerialized>;
    layers: Array<LayerSerialized>;
}

export enum PostType {
    visual = "visual",
}

export interface AssetBackend {
    creator?: {
        id: string;
    };
    key: string;
    id: string;
    mediaType?: string;
    url: string;
    name: string;
    extension: string;
    ppiUrl?: string | undefined;
    ppiExtension?: string | undefined;
}

export interface ImageAssetBackend extends AssetBackend {
    height: number;
    width: number;
    calcHeight: number;
    calcWidth: number;
}

export interface VideoAssetBackend extends AssetBackend {
    duration: number;
    width: number;
    height: number;
    calcWidth: number;
    calcHeight: number;
    chromaKey: {
        a: number;
        b: number;
        g: number;
        r: number;
    };
    dontRestartWhenGoingBack: boolean;
    loop: boolean;
    playbackMode?: 'loop' | 'pingpong' | 'scroll' | 'once';
    muted: boolean;
    tolerance: number;
    transparent: boolean;
    areaX: number;
    areaY: number;
    rotation: number;
    zoom: number;
    autoplay: boolean;
}

export enum ThemeColor {
    light = "light",
    dark = "dark",
}

export enum CustomThemeColor {
    purple = "purple",
    pink = "pink",
    mint = "mint",
    yellow = "yellow",
    navy = "navy",
    olive = "olive",
    graphite = "graphite",
    aubergine = "aubergine",
    black = "black",
    greyMarl = "grey-marl",
    gray = "gray",
    offWhite = "off-white",
    white = "white",
}

export interface PostMetadataSerialized {
    type: PostType;
    title: string;
    shortTitle: string;
    subtitle: string;
    credits: string;
    contact?: string;
    sources: string;
    license: string;

    thumbnailResized?: ImageAssetBackend;
    thumbnail?: string;
    previewImage?: ImageAssetBackend;
    previewVideo?: VideoAssetBackend;
    titleImage?: ImageAssetBackend;
    startButtonImage?: ImageAssetBackend;
    hideCloseButton?: boolean;

    theme: ThemeColor;
    iconsColor: CustomThemeColor;
    iconsBgColor: CustomThemeColor;
    legacy?: boolean;
    hidden?: boolean;
    promotionTag?: boolean;
    hasIframe?: boolean;
    iframeUrl?: string;
    hideIntroOverlay?: boolean;
}

export interface ProfileSerialized {
    avatar: string;
    avatarKey: string;
    id: string;
    firstName: string;
    lastName: string;
    description: string;
    position: string;
}

export enum PublishState {
    published = "published",
    unpublished = "unpublished",
    publishersNotSet = "publishers_not_set",
}

export enum ReviewState {
    created = "created",
    requested = "requested",
    rejected = "rejected",
    approved = "approved",
    reviewersNotSet = "reviewers_not_set",
}

export interface PostSerialized {
    tenants: Array<RealTenantId>;
    id: string;
    slug: string;
    title: string;
    data: PresentationSerialized;
    metadata: PostMetadataSerialized;
    creator?: {
        id: string;
    };
    authors?: Array<ProfileSerialized>;
    description: string;
    reviewers?: Array<ProfileSerialized>;
    publishers?: Array<ProfileSerialized>;
    authorIds: Array<string>;
    reviewerIds: Array<string>;
    publisherIds: Array<string>;
    publishedAt?: string;
    sortingDate?: string | null;
    publishState: PublishState;
    reviewState: ReviewState;
    visibility: PostVisibility;
}

export interface TemplateFrame {
    textSlideIds: string[];
    textLayerIds: string[];
    imageLayerIds: string[];
}

export interface TemplateStructure {
    readonly frames: TemplateFrame[];
}

export interface PostBackend extends PostSerialized {
    createdAt: string;
    updatedAt: string;
    templateStructure: TemplateStructure;
}

export interface PostJson {
    categoryName?: string;
    categoryId?: string;
    subcategoryId?: string;
    subcategoryName?: string;
    data: PostBackend;
}

export enum EPublicTemplateType {
    STANDARD = "STANDARD",
    AI = "AI",
}

export interface PublicTemplate {
    imageSearchTerms: string[][];
    id: number;
    json: PostJson;
    subCategoryName: string | null;
    noOfSlides?: number;
    category?: { id?: string; name?: string } | null;
}

export interface CategoryTree {
    category_id: string;
    category_name: string;
    children: CategoryTree[];
}

export interface CustomFonts {
    textFontFamily?: string;
    headlineFontFamily?: string;
}

/**
 * Scrolly type for the TemplateEditor context.
 * Maps to the admin panel's UserScrolly with typed JSON.
 */
export interface Scrolly {
    id: number | string;
    publicKey: string;
    json: PostJson;
}
